<script lang="ts">
  import { createEventDispatcher } from "svelte";
  import { _ } from "svelte-i18n";
  import { onMount } from "svelte";
  import Map from "ol/Map";
  import View from "ol/View";
  import { unByKey } from "ol/Observable";
  import type { EventsKey } from "ol/events";
  import type BaseLayer from "ol/layer/Base";
  import { dwdValueLayer } from "../layers/dwd";
  import { labelsOnly } from "../layers/vector";
  import { get } from "svelte/store";
  import { capTimeIndicator, mapBaseLayer } from "../stores";
  const dispatch = createEventDispatcher();
  export let layerManager;
  export let layer;
  export let label;
  /**
   * Whether this tile's map is a stand-in rather than the thing it advertises.
   *
   * The 3D cell view is a MapLibre map; every tile here is an OpenLayers one,
   * built by the same factory. So the tile for it shows the flat basemap and no
   * storms at all (a black rectangle with a border on it), and the reader has
   * no way to tell an approximate preview from an empty layer. Frosted and labelled, it stops making a claim it cannot keep:
   * the real map is built when the tile is tapped.
   */
  export let preview = false;

  /**
   * Whether the switcher is open. The tiles mount with the app, hidden, and
   * stay mounted; a map attached to a hidden tile still schedules a frame on
   * every change of the View it follows, so nothing is attached until the
   * switcher shows them.
   */
  export let open = false;

  let className = "";
  export { className as class };
  let uniqueID =
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15);

  onMount(async () => {
    dispatch("mount", {
      id: `map-${uniqueID}`,
      layer: layer,
    });
  });

  /*
   * A capability's own map is pointed at its tile by the switcher as it opens
   * (McLayerSwitcher.openLayerswitcher), not here at mount: attached to a
   * hidden tile for the whole session, the lightning and precipitation maps
   * would draw two empty frames for every frame of the real map. Only the tile
   * that has no capability map to borrow builds one, and attaches it while
   * the switcher is open.
   */
  function mapInit(node, opened: boolean) {
    if (preview) return decorativeMap(node, opened);
    return undefined;
  }

  /**
   * A throwaway map for a tile whose capability cannot draw one.
   *
   * The switcher hands a capability's single map to a single element, so
   * two tiles cannot both show the radar, and the 3D one has no OpenLayers
   * map of its own to hand over at all. So it gets its own map built here
   * instead of a blank behind the frosting: the basemap the app is on, the
   * newest radar frame over it, and the place names over that. That is the
   * rain and thunderstorms tile's map, as near as OpenLayers can draw the 3D
   * one.
   *
   * Decoration, and treated as such: no interactions and no controls. It
   * exists so the tile reads as a map rather than as a hole. If the radar has
   * not loaded yet it simply shows the basemap, which is what the other tiles
   * do too.
   *
   * It does follow the map, as every other tile does. Those draw through the
   * one View the app's maps share, so a pan moves all of them. A View of its
   * own copied once at mount (with the app, before the reader has moved
   * anything) would sit still on the opening view while the tiles around it
   * pan.
   *
   * Following rather than sharing: a map writes its own size into its View
   * (`setViewportSize`) whenever it is resized or handed the View, and this
   * one mounts hidden at 0x0 and lives as long as the app, so on the shared
   * View it would keep overwriting the size the real map's constraints are
   * worked out from. A View of its own, kept in step, has nothing to write
   * over. The shared View is itself replaced when the rotation setting
   * changes (see App.svelte), so the map's `change:view` rebinds it.
   *
   * Attached and following only while the switcher is open: shut, it would
   * still take a frame for every frame of the real map.
   */
  function decorativeMap(node, opened: boolean) {
    const own = new View({ center: [0, 0], zoom: 7 });
    const preview_ = new Map({
      layers: [layerManager.baseLayerFactory(get(mapBaseLayer)), labelsOnly()],
      controls: [],
      interactions: [],
      view: own,
    });

    // Wherever the reader is looking, so the tile shows their weather rather
    // than a fixed corner of the country, and wherever they look next.
    const source: Map | undefined = layerManager.maps[0];
    let viewKeys: EventsKey[] = [];
    let visible = false;
    const follow = () => {
      unByKey(viewKeys);
      viewKeys = [];
      const shared = source?.getView();
      if (!shared || !visible) return;
      const sync = () => {
        const center = shared.getCenter();
        const resolution = shared.getResolution();
        if (center) own.setCenter(center);
        if (resolution !== undefined) own.setResolution(resolution);
        own.setRotation(shared.getRotation());
      };
      sync();
      viewKeys = shared.on(["change:center", "change:resolution", "change:rotation"], sync) as EventsKey[];
    };
    const mapKey = source?.on("change:view", follow);
    const show = (now: boolean) => {
      if (now === visible) return;
      visible = now;
      preview_.setTarget(now ? node : undefined);
      follow();
    };
    show(opened);

    /*
     * The radar goes on when there is radar, which is not now.
     *
     * These tiles mount with the app, not when the switcher opens (the panel
     * is built hidden), so at this point the grid has not been fetched
     * and there is no frame to draw. Built once and left alone, the tile would
     * be a basemap for the rest of the session. Following the clock instead
     * puts the radar on as soon as it lands, and keeps it roughly current
     * after that for nothing: the check is a string compare.
     */
    let shown: string | null = null;
    let tiles: BaseLayer | null = null;
    const unsubscribe = capTimeIndicator.subscribe(() => {
      const radar = layerManager.getCapability("radar");
      const step = radar?.getMostRecentObservation?.();
      const frame = step === undefined ? null : radar?.clientGrid?.[step];
      if (!frame?.tile_id || frame.tile_id === shown) return;
      shown = frame.tile_id;
      if (tiles) {
        preview_.removeLayer(tiles);
        tiles.dispose();
      }
      [tiles] = dwdValueLayer(frame);
      preview_.addLayer(tiles);
    });

    return {
      update: show,
      destroy() {
        unsubscribe();
        unByKey(viewKeys);
        if (mapKey) unByKey(mapKey);
        preview_.setTarget(undefined);
      },
    };
  }

  let _down = false;
  let lastX = 0;
  let lastY = 0;
  function mouseDown(evt) {
    _down = true;
    lastX = evt.clientX;
    lastY = evt.clientY;
  }

  function mouseUp(evt) {
    if (
      Math.abs(evt.clientX - lastX) < 10 &&
      Math.abs(evt.clientY - lastY) < 10
    ) {
      dispatch("changeLayer", layer);
    }
    _down = false;
  }
</script>

<style>
  :global(.miniMap) {
    width: 100%;
    height: 100%;
    padding: 0;
    margin: 0;
    top: 0;
    left: 0;
  }
  :global(.miniMap > div:nth-child(1) > div:nth-child(3) > div:nth-child(3)) {
    display: none;
  }
  :global(.miniMap .ol-control) {
    display: none;
  }

  /* The frosting over a tile whose map is not the layer it stands for: enough
     blur to say the picture is a stand-in, little enough that the place
     names under it still say where. */
  .frost {
    position: absolute;
    inset: 0;
    z-index: 90;
    /* The tile clips to its card radius, but a backdrop-filter escapes an
       ancestor's *rounded* clip in both WebKit and Chromium: only the square
       border box survives, so the frost would poke out at the corners. It carries
       the radius itself; `inherit` keeps it tied to the card's. */
    border-radius: inherit;
    -webkit-backdrop-filter: blur(6px) saturate(1.2);
    backdrop-filter: blur(6px) saturate(1.2);
    /* A tint, not the panels' glass: at their 82% the map under it would be a
       black card, and the blur alone already says "stand-in". */
    background: color-mix(in srgb, var(--mc-glass-fill) 35%, transparent);
    pointer-events: none;
  }

  /* Where no blur is available the frosting would be a clear pane over a black
     map, so it becomes an opaque one instead. */
  @supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
    .frost {
      background: var(--mc-glass-fill-solid);
    }
  }

  .previewTag {
    position: absolute;
    top: 10px;
    left: 10px;
    z-index: 100;
    padding: 3px 8px;
    border-radius: var(--mc-radius-pill);
    background: var(--mc-label-fill);
    border: 1px solid rgba(255, 255, 255, 0.18);
    color: #fff;
    font: 700 10px/1 var(--mc-font);
    letter-spacing: 0.06em;
    text-transform: uppercase;
    pointer-events: none;
  }

  /* A dark caption capsule inset in the card. Deliberately no backdrop-filter:
     three of these sit over three live map canvases. */
  .label {
    position: absolute;
    left: 10px;
    right: 10px;
    bottom: 10px;
    top: auto;
    z-index: 100;
    display: block;
    margin: 0;
    padding: 8px 12px;
    border-radius: var(--mc-radius-inner);      /* concentric with the 22px card at a 10px inset */
    background: var(--mc-label-fill);
    border: 1px solid rgba(255, 255, 255, 0.12);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 2px 8px rgba(0, 0, 0, 0.25);
    color: #fff;
    font: 600 14px/1.2 var(--mc-font);
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    opacity: 1;
  }
</style>

<div
  id="map-{uniqueID}"
  class="miniMap"
  use:mapInit={open}
  on:mousedown={mouseDown}
  on:mouseup={mouseUp} />
{#if preview}
  <div class="frost"></div>
  <div class="previewTag">{$_("preview")}</div>
{/if}
<div class="label" on:mousedown={mouseDown} on:mouseup={mouseUp}>{label}</div>
